import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Rocket, Settings as SettingsIcon } from "lucide-react";

const STATUS = {
  ready_for_live: { label: "Ready for Live", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    text: "Everything is verified. Texts use your business name and homeowners can be sent your booking link." },
  setup_incomplete: { label: "Setup Incomplete", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    text: "You've started your own texting setup, but some items below still need attention. Until then RoofLead stays in safe testing mode." },
  safe_testing: { label: "Safe Testing Mode", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    text: "Calls and texts use the RoofLead testing identity. Add your own texting registration and complete the checklist to go live." },
};

export default function GoLive() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/org/go-live").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-8 text-slate-400" data-testid="go-live-loading">Loading…</div>;
  const s = STATUS[data.status];
  const done = data.items.filter((i) => i.ok).length;
  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="go-live-page">
      <h1 className="font-display text-4xl sm:text-5xl font-bold flex items-center gap-3">
        <Rocket className="w-8 h-8 text-orange-400" /> Go-live checklist
      </h1>
      <Card className="bg-slate-900 border-slate-800 p-6 mt-8">
        <div className="flex flex-wrap items-center gap-3">
          <Badge className={`border text-sm px-3 py-1 ${s.cls}`} data-testid="go-live-status">{s.label}</Badge>
          <span className="text-slate-400 text-sm" data-testid="go-live-progress">{done} of {data.items.length} ready</span>
        </div>
        <p className="text-slate-300 text-sm mt-3">{s.text}</p>
        {data.twilio_checked_at && (
          <p className="text-slate-500 text-xs mt-2">Twilio last verified {new Date(data.twilio_checked_at).toLocaleString()} — re-checked automatically every 6 hours and after any settings change.</p>
        )}
      </Card>
      <Card className="bg-slate-900 border-slate-800 p-2 mt-6">
        <ul className="divide-y divide-slate-800">
          {data.items.map((i) => (
            <li key={i.key} className="flex items-start gap-4 p-4" data-testid={`go-live-item-${i.key}`}>
              {i.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /> : <Circle className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <p className={i.ok ? "text-slate-100" : "text-slate-300"}>{i.label}</p>
                <p className={`text-xs mt-0.5 break-words ${i.ok ? "text-slate-500" : "text-amber-300/90"}`}>{i.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <Button asChild className="mt-6 bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold" data-testid="go-live-open-settings">
        <Link to="/app/settings"><SettingsIcon className="w-4 h-4 mr-2" /> Open Settings</Link>
      </Button>
    </div>
  );
}
